import { useState } from 'react';
import { BookingCalendar } from './BookingCalendar';
import { useUTMTracking } from '../../../hooks/useUTMTracking';
import { useAnalytics } from '../../../hooks/useAnalytics';
import ChatBookingCallout from './ChatBookingCallout';
import { supabase } from '../../../utils/supabase';

export default function Contact() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);
  const [smsConsent, setSmsConsent] = useState(false);
  const { utmParams } = useUTMTracking();
  const { trackFormSubmit } = useAnalytics();
  const [activeTab, setActiveTab] = useState<'calendar' | 'contact'>('calendar');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === 'message') {
      if (value.length > 500) return;
      setCharCount(value.length);
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (charCount > 500) return;
    setIsSubmitting(true);
    setSubmitStatus('idle');
    try {
      const body = new URLSearchParams();
      body.append('firstName', formData.firstName);
      body.append('lastName', formData.lastName);
      body.append('email', formData.email);
      body.append('phone', formData.phone);
      body.append('message', formData.message);
      
      // Add UTM params
      if (utmParams.traffic_source) body.append('traffic_source', utmParams.traffic_source);
      if (utmParams.utm_source) body.append('utm_source', utmParams.utm_source);
      if (utmParams.utm_medium) body.append('utm_medium', utmParams.utm_medium);
      if (utmParams.utm_campaign) body.append('utm_campaign', utmParams.utm_campaign);
      if (utmParams.utm_term) body.append('utm_term', utmParams.utm_term);
      if (utmParams.utm_content) body.append('utm_content', utmParams.utm_content);
      if (utmParams.referrer) body.append('referrer', utmParams.referrer);

      const res = await fetch('https://readdy.ai/api/form/d6pp7t6a739gopmlgv00', {
        method: 'POST',
        mode: 'cors',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setSubmitStatus('success');
        setFormData({ firstName: '', lastName: '', email: '', phone: '', message: '' });
        setCharCount(0);
        
        // Track form submission
        trackFormSubmit('Contact Form');

        // Fire-and-forget: Send email notification
        const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
        fetch(`${supabaseUrl}/functions/v1/send-email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'new_lead',
            lead: {
              name: `${formData.firstName} ${formData.lastName}`,
              email: formData.email,
              phone: formData.phone,
              service: 'Contact Form Inquiry',
              message: formData.message,
            },
          }),
        }).catch(() => {
          // Silent fail - don't block user experience
        });

        // Insert lead into Supabase with 24-hour follow-up scheduled
        const followUpTime = new Date();
        followUpTime.setHours(followUpTime.getHours() + 24);

        supabase
          .from('leads')
          .insert({
            first_name: formData.firstName,
            last_name: formData.lastName,
            email: formData.email,
            phone: formData.phone,
            notes: formData.message,
            source: utmParams.traffic_source || 'Contact Form',
            stage: 'new',
            follow_up_scheduled_at: followUpTime.toISOString(),
            follow_up_sent: false,
          })
          .then(() => {
            // Silent success
          })
          .catch(() => {
            // Silent fail - don't block user experience
          });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-[#F9F7F4]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-[#1A3A52] mb-4">
            Let's Connect
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Ready to protect your family's future? Schedule a consultation or send us a message — we're here to help.
          </p>
        </div>

        {/* Online Appointment Callout */}
        <div className="mb-10">
          <a 
            href="/book-appointment"
            className="block bg-gradient-to-r from-[#D97642] to-[#E88A5C] text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.01] p-6"
          >
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-calendar-check-line text-2xl"></i>
                </div>
                <div className="text-center sm:text-left">
                  <h3 className="text-xl font-bold mb-1">Book Your Free Appointment Online</h3>
                  <p className="text-white/90 text-sm">Choose phone, video, or in-person — schedule at your convenience</p>
                </div>
              </div>
              <div className="flex items-center gap-2 font-semibold whitespace-nowrap">
                <span>Book Now</span>
                <i className="ri-arrow-right-line"></i>
              </div>
            </div>
          </a>
        </div>

        {/* Tab Switcher */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex bg-white rounded-full p-1.5 shadow-lg">
            <button
              onClick={() => setActiveTab('calendar')}
              className={`px-8 py-3 rounded-full font-semibold transition-all duration-300 whitespace-nowrap ${
                activeTab === 'calendar'
                  ? 'bg-[#1A3A52] text-white'
                  : 'text-gray-600 hover:text-[#1A3A52]'
              }`}
            >
              <i className="ri-calendar-line mr-2"></i>
              Book Appointment
            </button>
            <button
              onClick={() => setActiveTab('contact')}
              className={`px-8 py-3 rounded-full font-semibold transition-all duration-300 whitespace-nowrap ${
                activeTab === 'contact'
                  ? 'bg-[#1A3A52] text-white'
                  : 'text-gray-600 hover:text-[#1A3A52]'
              }`}
            >
              <i className="ri-message-3-line mr-2"></i>
              Send Message
            </button>
          </div>
        </div>

        {/* Missed Call Text Back Banner */}
        <div className="max-w-[900px] mx-auto mb-10 md:mb-14">
          <div className="bg-gradient-to-r from-[#D97642] to-[#C26638] rounded-2xl p-6 md:p-8 shadow-lg">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-5">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-white/20 shrink-0">
                <i className="ri-message-3-fill text-[28px] text-white"></i>
              </div>
              <div className="flex-1">
                <h3 className="text-[18px] md:text-[20px] font-bold text-white mb-2">
                  We'll Text You Back
                </h3>
                <p className="text-[14px] md:text-[15px] text-white/90 leading-relaxed mb-3">
                  If you call and we miss you, you'll automatically receive a text message:
                </p>
                <div className="bg-white/15 backdrop-blur-sm rounded-lg px-4 py-3 border border-white/20 mb-4">
                  <p className="text-[13px] md:text-[14px] text-white font-medium italic">
                    "Sorry we missed your call. Reply YES if you want a quick life insurance quote."
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href="tel:+13176482983"
                    className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-white text-[#D97642] text-[14px] font-semibold rounded-lg hover:bg-gray-50 transition-all shadow-md cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-phone-fill text-[16px]"></i>
                    Call (317) 648-2983
                  </a>
                  <a
                    href="sms:+13176482983"
                    className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-white/10 text-white text-[14px] font-semibold rounded-lg hover:bg-white/20 transition-all border border-white/30 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-chat-1-fill text-[16px]"></i>
                    Text Us Now
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Chat Booking Callout */}
        <div className="max-w-[900px] mx-auto mb-10 md:mb-14">
          <ChatBookingCallout />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 items-start">
          {/* Left — Contact Info + Map */}
          <div className="flex flex-col gap-8">
            {/* Info Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#D97642]/10 shrink-0">
                  <i className="ri-phone-fill text-[20px] text-[#D97642]"></i>
                </div>
                <div>
                  <p className="text-[13px] font-semibold text-[#1A3A52] uppercase tracking-wide mb-1">Phone</p>
                  <a href="tel:+13176482983" className="text-[15px] text-[#4A5568] hover:text-[#D97642] transition-colors cursor-pointer font-medium">
                    (317) 648-2983
                  </a>
                  <p className="text-[12px] text-[#718096] mt-0.5">Mon–Fri 9AM–5PM EST · Sat 8AM–1PM EST</p>
                </div>
              </div>

              <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#D97642]/10 shrink-0">
                  <i className="ri-mail-fill text-[20px] text-[#D97642]"></i>
                </div>
                <div>
                  <p className="text-[13px] font-semibold text-[#1A3A52] uppercase tracking-wide mb-1">Email</p>
                  <a href="mailto:info@hoosierlifeinsurance.com" className="text-[15px] text-[#4A5568] hover:text-[#D97642] transition-colors cursor-pointer font-medium">
                    info@hoosierlifeinsurance.com
                  </a>
                  <p className="text-[12px] text-[#718096] mt-0.5">We reply within 24 hours</p>
                </div>
              </div>

              <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#D97642]/10 shrink-0">
                  <i className="ri-map-pin-fill text-[20px] text-[#D97642]"></i>
                </div>
                <div>
                  <p className="text-[13px] font-semibold text-[#1A3A52] uppercase tracking-wide mb-1">Office</p>
                  <p className="text-[15px] text-[#4A5568] font-medium leading-snug">
                    5625 German Church Rd<br />Indianapolis, IN 46235
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#D97642]/10 shrink-0">
                  <i className="ri-calendar-check-fill text-[20px] text-[#D97642]"></i>
                </div>
                <div>
                  <p className="text-[13px] font-semibold text-[#1A3A52] uppercase tracking-wide mb-1">Free Consultation</p>
                  <p className="text-[15px] text-[#4A5568] font-medium leading-snug">
                    In-person, phone,<br />or virtual — your choice
                  </p>
                </div>
              </div>
            </div>

            {/* Google Map */}
            <div className="w-full rounded-2xl overflow-hidden shadow-md border border-gray-100">
              <iframe
                title="Hoosier Life Insurance — Indianapolis Metro Service Area"
                src="https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d386000!2d-86.1581!3d39.9012!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sen!2sus!4v1700000000099!5m2!1sen!2sus"
                width="100%"
                height="320"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
              <div className="bg-white px-5 py-4 border-t border-gray-100">
                <div className="flex items-center gap-2 text-[13px] text-gray-500 mb-3">
                  <i className="ri-map-pin-2-line text-[#D97642] text-[16px]"></i>
                  <span className="font-medium text-[#1A3A52]">Serving all of Central Indiana</span>
                  <span>— consultations available by phone, video, or in-person by appointment</span>
                </div>
                <a
                  href="https://www.google.com/maps/search/life+insurance+Indianapolis+IN"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-[14px] font-semibold text-[#D97642] hover:text-[#C26638] transition-colors cursor-pointer"
                >
                  <i className="ri-map-pin-line text-[18px]"></i>
                  View Service Area →
                </a>
              </div>
            </div>
          </div>

          {/* Right — Booking Calendar + Contact Form */}
          <div className="flex flex-col gap-6">
            {/* Schedule a Consultation */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-7 md:p-10">
              <BookingCalendar
                timeslotsApi="https://readdy.ai/api/public/calendar/timeslots/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1"
                appointmentsApi="https://readdy.ai/api/public/calendar/appointments/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1"
                texts={{
                  heading: 'Schedule a Consultation',
                  step0Title: 'Select a Date',
                  step1Title: 'Choose a Time',
                  step2Title: 'Confirm Your Appointment',
                  selectDate: 'Select Date',
                  selectTime: 'Continue to Time Selection',
                  reviewAndBook: 'Confirm Booking',
                  previousMonth: 'Previous',
                  nextMonth: 'Next',
                  noSlotsAvailable: 'No available slots for this date',
                  selectedDateTime: 'Selected Date & Time',
                  modifyTime: 'Change Time',
                  customerName: 'Your Name',
                  customerEmail: 'Email Address',
                  customerPhone: 'Phone Number',
                  notes: 'Additional Notes (Optional)',
                  submitButton: 'Confirm Appointment',
                  submitting: 'Booking...',
                  successMessage: "Appointment Confirmed! We'll call you shortly.",
                  errorMessage: 'Booking failed. Please try again.',
                  backButton: 'Back to Calendar',
                  required: '*',
                }}
                containerStyle=""
              />
            </div>

            {/* Contact Form */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-7 md:p-10">
              <h3 className="text-[20px] md:text-[24px] font-bold text-[#1A3A52] mb-2">Send Us a Message</h3>
              <p className="text-[14px] text-[#718096] mb-7">Fill out the form and a local advisor will be in touch shortly.</p>

              {submitStatus === 'success' ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-100 mb-4">
                    <i className="ri-check-line text-[32px] text-green-600"></i>
                  </div>
                  <h4 className="text-[20px] font-bold text-[#1A3A52] mb-2">Message Sent!</h4>
                  <p className="text-[15px] text-[#4A5568]">Thank you for reaching out. We'll get back to you within 24 hours.</p>
                  <button
                    onClick={() => setSubmitStatus('idle')}
                    className="mt-6 px-6 py-2.5 rounded-lg border border-[#D97642] text-[#D97642] text-[14px] font-medium hover:bg-[#D97642] hover:text-white transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form id="contact-us-form" data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-4">
                  {/* UTM Tracking Info */}
                  {utmParams.traffic_source && (
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-100 mb-2">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 flex items-center justify-center rounded-full bg-blue-100 shrink-0">
                          <i className="ri-information-line text-[16px] text-blue-600"></i>
                        </div>
                        <div className="flex-1">
                          <p className="text-[13px] font-semibold text-[#1A3A52] mb-1">We see you came from {utmParams.traffic_source}</p>
                          <p className="text-[12px] text-[#718096]">
                            Your source is automatically tracked to help us serve you better.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">First Name <span className="text-[#D97642]">*</span></label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                        placeholder="Jane"
                        className="w-full px-4 py-3 rounded-lg border border-gray-200 text-[14px] text-[#1A3A52] placeholder-gray-400 focus:outline-none focus:border-[#D97642] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">Last Name <span className="text-[#D97642]">*</span></label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                        placeholder="Smith"
                        className="w-full px-4 py-3 rounded-lg border border-gray-200 text-[14px] text-[#1A3A52] placeholder-gray-400 focus:outline-none focus:border-[#D97642] transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">Email Address <span className="text-[#D97642]">*</span></label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="jane@example.com"
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 text-[14px] text-[#1A3A52] placeholder-gray-400 focus:outline-none focus:border-[#D97642] transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">Phone Number</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="(317) 000-0000"
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 text-[14px] text-[#1A3A52] placeholder-gray-400 focus:outline-none focus:border-[#D97642] transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">
                      Message <span className="text-[#D97642]">*</span>
                      <span className="ml-2 text-[12px] font-normal text-[#718096]">({charCount}/500)</span>
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={4}
                      maxLength={500}
                      placeholder="Tell us how we can help — questions about coverage, a free quote, or scheduling a consultation..."
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 text-[14px] text-[#1A3A52] placeholder-gray-400 focus:outline-none focus:border-[#D97642] transition-colors resize-none"
                    />
                    {charCount >= 500 && (
                      <p className="text-[12px] text-red-500 mt-1">Maximum 500 characters reached.</p>
                    )}
                  </div>

                  {submitStatus === 'error' && (
                    <p className="text-[13px] text-red-500">Something went wrong. Please try again.</p>
                  )}

                  {/* SMS Consent Checkbox — A2P Required */}
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name="sms_consent"
                        checked={smsConsent}
                        onChange={(e) => setSmsConsent(e.target.checked)}
                        required
                        className="mt-0.5 w-4 h-4 accent-[#D97642] shrink-0 cursor-pointer"
                      />
                      <span className="text-[12px] text-[#4A5568] leading-relaxed">
                        <strong className="text-[#1A3A52]">I agree to receive SMS/text messages</strong> from Hoosier Life Insurance at the phone number provided. Message frequency varies. Message &amp; data rates may apply. Reply <strong>STOP</strong> to opt out at any time. Reply <strong>HELP</strong> for help. Consent is not a condition of purchase. See our{' '}
                        <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-[#D97642] underline hover:text-[#C26638]">Privacy Policy</a> for full details.
                      </span>
                    </label>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting || charCount > 500 || !smsConsent}
                    className="w-full py-3.5 bg-[#D97642] text-white text-[15px] font-semibold rounded-lg hover:bg-[#C26638] transition-all hover:shadow-lg flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 whitespace-nowrap mt-1"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                    <i className="ri-send-plane-fill text-[17px]"></i>
                  </button>

                  <p className="text-[12px] text-[#718096] text-center">
                    We respect your privacy. Your information is never shared or sold.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}